import {GameStateEnums} from '../../../../../shared/GameEnums'
import {PopupInformation} from '@colonist/game-base-types'
import {GameColor} from '../../GameColor'
import {SectionBgAdaptiveView} from '../../ui_canvas_engine/SectionBgViewAdaptive'
import {UIGameCardContainerView} from '../ui_game_card/UIGameCardContainerView'
import {UIGameManager} from '../UIGameManager'
import {UIGamePopupConfirmationButtons} from './UIGamePopupConfirmationButtons'
import {TextView} from '../../ui_canvas_engine/TextView'
import {UIGamePopupCondensedCardInformationController} from './UIGamePopupCondensedCardInformationController'
import {TranslationObject, getNestedTranslation} from '@colonist/i18n'
import {UIGamePopupInformationView} from './UIGamePopupInformationView'
import {SelectCardToDiscardFormat} from '@colonist/game-card-types'
import {UIGameMechanic} from '../UIGameEventController'
import {ArrayUtils} from '@colonist/utils'
import {CardEnum, MechanicProgressCardsState} from '@colonist/game-states'
import {MechanicValidatorProgressCards} from '@colonist/game-mechanic-validators'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums

export abstract class UIGamePopupDiscardCards extends SectionBgAdaptiveView implements UIGameMechanic {
    protected readonly uiGameManager: UIGameManager
    protected readonly selectDiscardCardFormat: SelectCardToDiscardFormat
    private titleTextBox: TextView
    private bodyTextBox: TextView
    private selectedCardContainer: UIGameCardContainerView
    protected confirmationButtons: UIGamePopupConfirmationButtons
    protected playerCardContainer: UIGameCardContainerView
    private condensedCardInformationController: UIGamePopupCondensedCardInformationController

    private readonly amountToDiscard: number
    private readonly isCondensedInformationActive: boolean

    private popupInformationView: UIGamePopupInformationView
    private readonly progressCardsValidator: MechanicValidatorProgressCards | undefined

    constructor(uiGameManager: UIGameManager, selectCardFormat: SelectCardToDiscardFormat, selectedCardsH: number, progressCardsState: MechanicProgressCardsState | undefined) {
        super()
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)
        this.selectDiscardCardFormat = selectCardFormat
        if(progressCardsState) this.progressCardsValidator = new MechanicValidatorProgressCards(this.uiGameManager.gameController.getPlayerValidators(), this.uiGameManager.gameController.mapValidator, progressCardsState)
        this.amountToDiscard = selectCardFormat.selectCardFormat.amountOfCardsToSelect
        this.isCondensedInformationActive = this.selectDiscardCardFormat.showCondensedCardInformation

        const canvas = this.uiGameManager.canvasController
        canvas.app.stage.addChild(this)

        this.createPopupInformationView(selectCardFormat)
        this.createSelectedCardsView(selectedCardsH)
        this.createConfirmationButtons()
        this.createCondensedCardInformation()

        const horizontalBuffer = 0
        const longestTextHeight = this.condensedCardInformationController.getLongestTextHeight(this.selectDiscardCardFormat.selectCardFormat.validCardsToSelect)
        const verticalBuffer = longestTextHeight < this.confirmationButtons.height ? 0 : longestTextHeight - this.confirmationButtons.height

        this.buildContainer(horizontalBuffer, verticalBuffer)
        this.setContainerY()
        this.createPlayerCardContainer()
        this.updateTopText()
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        if(this.parent == undefined) return
        switch(eventType) {
            case UIGameEventEnums.PlayerControllerStateUpdated: this.checkIfCardsAreInSync(); break
            default: break
        }
    }

    private createPopupInformationView(selectCardFormat: SelectCardToDiscardFormat) {
        const popupInformation: PopupInformation = {
            imageTextureName: selectCardFormat.imageName,
            title: selectCardFormat.title,
            body: selectCardFormat.body,
        }
        const canvas = this.uiGameManager.canvasController
        const w = canvas.popupWidth
        this.popupInformationView = new UIGamePopupInformationView(this.uiGameManager, 0, 0, w, popupInformation, this)
        this.titleTextBox = this.popupInformationView.title
        this.bodyTextBox = this.popupInformationView.body
    }

    private createSelectedCardsView(h: number) {
        const cardChangeAction = () => {
            if(this.parent == undefined) return
            this.updateTopText()
            this.updateCheckMark()
            this.sendSelectedCardsState()
        }

        const canvas = this.uiGameManager.canvasController
        const w = canvas.popupWidth
        const selectCardContainerY = this.popupInformationView.y + this.popupInformationView.height
        this.selectedCardContainer = new UIGameCardContainerView(this.uiGameManager, 0, selectCardContainerY, w, h, this)
        this.selectedCardContainer.setCardAddAction(cardChangeAction)
        this.selectedCardContainer.setCardRemoveAction(cardChangeAction)
    }

    private sendSelectedCardsState() {
        this.uiGameManager.socketGameSend.selectCardsState(this.selectedCardContainer.cardInventory.getAllCardEnums())
    }

    protected abstract createConfirmationButtons()

    private createCondensedCardInformation() {
        const padding = this.uiGameManager.canvasController.itemSize * 0.1
        const textX = padding
        const textW = this.width - textX - (this.width - this.confirmationButtons.checkButton.x) - padding
        const textY = this.selectedCardContainer.y + this.selectedCardContainer.height
        this.condensedCardInformationController = new UIGamePopupCondensedCardInformationController(this.uiGameManager, this, textX, textY, textW, this.selectDiscardCardFormat.selectCardFormat.validCardsToSelect)
    }

    protected buildContainer(horizontalBuffer: number = 0, verticalBuffer: number = 0) {
        this.drawTextureBgAroundContent(horizontalBuffer, verticalBuffer)
        this.confirmationButtons.y = this.absoluteHeight - this.confirmationButtons.height
    }

    protected abstract setContainerY()

    private updateTopText() {
        const currentTitleText = this.selectDiscardCardFormat.title
        const selectedCardsAmount = this.selectedCardContainer.cardInventory.cardViews.length
        const selectedCardsText: TranslationObject = {
            key: 'strings:game.prompts.discardCardDenominator',
            options: {
                selectedCardsAmount: selectedCardsAmount,
                amountToDiscard: this.amountToDiscard,
            },
        }

        const text = getNestedTranslation({key: 'strings:colonistCommon.utils.#append'}, {
            value1: currentTitleText,
            value2: selectedCardsText,
        })

        const color = this.hasEnoughCards() ? GameColor.getColor(GameColor.Color.GreenDark) : GameColor.getColor(GameColor.Color.RedDark)
        this.popupInformationView.title.setText(text)
        this.titleTextBox.changeStyle({fill: color})
    }

    private hasEnoughCards(): boolean {
        return this.selectedCardContainer.cardInventory.cardViews.length == this.amountToDiscard
    }

    private updateCheckMark() {
        this.confirmationButtons.checkButton.inactivateOpacity()
        if(!this.hasEnoughCards()) return

        this.confirmationButtons.checkButton.activateOpacity()
    }

    protected checkAction() {
        if(!this.hasEnoughCards()) return
        const selectedCards = this.selectedCardContainer.cardInventory.getAllCardEnums()
        this.uiGameManager.socketGameSend.selectCards(selectedCards)
        this.closeUI()
    }

    private addCard(cardEnum: CardEnum) {
        if(!ArrayUtils.elementInArray(this.selectDiscardCardFormat.selectCardFormat.validCardsToSelect, cardEnum)) return
        if(this.selectDiscardCardFormat.selectCardFormat.amountOfCardsToSelect == 1) this.resetCardSelection()
        if(this.hasEnoughCards()) return
        if(!this.canPlayerAddCard(cardEnum)) return

        this.selectedCardContainer.addNewCardOfType(cardEnum)
        const keepPlayerCardsStaticForBetterUX = this.selectDiscardCardFormat.selectCardFormat.amountOfCardsToSelect == 1
        if(!keepPlayerCardsStaticForBetterUX) this.playerCardContainer.removeCard(cardEnum)

        if(this.isCondensedInformationActive) {
            this.condensedCardInformationController.removeSelectedCardInformation()
            this.condensedCardInformationController.showSelectedCardInformation(cardEnum)
        }
    }

    closeUI() {
        this.playerCardContainer.closeUI()
        if(this.parent == undefined) return
        this.parent.removeChild(this)
        this.uiGameManager.eventController.sendEvent(undefined, GameStateEnums.UIGameEventEnums.PopupActive)
    }

    protected abstract createPlayerCardContainerView()

    private createPlayerCardContainer() {
        this.createPlayerCardContainerView()

        const cardChangeAction = () => {
            this.updateTopText()
            this.updateCheckMark()
        }

        this.selectedCardContainer.setCardClickAction((cardEnum: CardEnum) => {
            this.selectedCardContainer.removeCard(cardEnum)
            const keepPlayerCardsStaticForBetterUX = this.selectDiscardCardFormat.selectCardFormat.amountOfCardsToSelect == 1
            if(!keepPlayerCardsStaticForBetterUX) this.playerCardContainer.addNewCardOfType(cardEnum)
            this.condensedCardInformationController.removeSelectedCardInformation()
            cardChangeAction()
        })

        const addCard = (cardEnum: CardEnum) => {
            this.addCard(cardEnum)
        }

        this.playerCardContainer.setCardClickAction((cardEnum: CardEnum) => {
            addCard(cardEnum)
        })

        this.updatePlayerCards()
    }

    private updatePlayerCards() {
        const allCardsInHand = this.uiGameManager.gameController.getAllCardsInHand(this.uiGameManager.gameController.myColor)
        for(const card of allCardsInHand) {
            this.playerCardContainer.addNewCardOfType(card, false)
        }
        this.playerCardContainer.fadeInAllCardsExcludingCardsOfType(this.selectDiscardCardFormat.selectCardFormat.validCardsToSelect)
    }

    private canPlayerAddCard(cardEnum: CardEnum): boolean {
        const myColor = this.uiGameManager.gameController.myColor
        const currentSelectedAmount = this.selectedCardContainer.cardInventory.getCardTypeCount(cardEnum)

        const currentResourceCardAmount = this.uiGameManager.gameController.totalResourceCardOfType(myColor, cardEnum)
        if(currentResourceCardAmount > currentSelectedAmount) return true

        const currentProgressCardsAmount = this.progressCardsValidator?.getPlayersProgressCardCountForCard(myColor, cardEnum) ?? 0
        if(currentProgressCardsAmount > currentSelectedAmount) return true

        return false
    }

    private resetCardSelection() {
        this.selectedCardContainer.removeAllCards()
        this.playerCardContainer.removeAllCards()
        this.updatePlayerCards()
    }

    private checkIfCardsAreInSync() {
        const allCardsInPopup = [...this.selectedCardContainer.cardInventory.getAllCardEnums(), ...this.playerCardContainer.cardInventory.getAllCardEnums()].sort()
        const allCardsInHand = this.uiGameManager.gameController.getAllCardsInHand(this.uiGameManager.gameController.myColor).sort()
        const areCardsInSync = ArrayUtils.arraysEqual(allCardsInPopup, allCardsInHand)
        if(areCardsInSync) return

        this.resetCardSelection()
    }
}
